'use client';

import './search-bar.scss';

import { ElementType, useEffect, useRef } from 'react';
import { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { debounce } from 'throttle-debounce';
import { z } from 'zod';

import { ButtonStyle } from '@sola/ark-core/atoms/button-style/button-style.js';
import {
  Prominence,
  Sizes,
  Text,
  Variants,
} from '@sola/ark-core/atoms/text/text.js';
import { Form } from '@sola/ark-core/molecules/form/form.js';
import { InputField } from '@sola/ark-core/molecules/input-field/input-field.js';
import {
  trackClick,
  trackSearch,
} from '@sola/tracking/components/datalayer.js';

export const SearchBarProps = z.object({
  className: z.string().optional(),
  seoLevel: z.number().int().optional(),
  title: z.string().optional(),
  titleSize: z.enum(['xs', 'sm', 'md', 'lg', 'xl', 'xxl']).optional(),
  placeholder: z.string().optional(),
  buttonText: z.string().optional(),
  defaultQuery: z.string().optional(),
  onQuerySubmit: z
    .function({ input: [z.string()], output: z.void() })
    .optional(),
  onQueryChange: z
    .function({ input: [z.string()], output: z.void() })
    .optional(),
  enableQuickSearch: z.boolean().optional(),
  focusOnLoad: z.boolean().optional(),
});

export type SearchBarProps = z.infer<typeof SearchBarProps>;

export const SearchBar: React.FC<SearchBarProps> = ({
  seoLevel = 3,
  title,
  titleSize = 'sm',
  placeholder,
  buttonText,
  defaultQuery,
  onQuerySubmit,
  onQueryChange,
  enableQuickSearch = false,
  focusOnLoad = true,
}) => {
  const { t } = useTranslation();
  const searchRef = useRef<HTMLInputElement>({} as HTMLInputElement);

  useEffect(() => {
    if (searchRef.current && searchRef.current.focus && focusOnLoad) {
      searchRef.current.focus();
    }
  }, []);

  const handleQueryChange = debounce(300, (queryString: string) => {
    if (onQueryChange) {
      onQueryChange(queryString);
    }
  });

  // We intend tracking to happen a little slower than results, to prevent tracking incomplete terms.
  const trackQueryChange = debounce(1500, (queryString: string) => {
    trackSearch(queryString);
  });

  const handleSubmit = (data: FieldValues) => {
    if (onQuerySubmit) {
      onQuerySubmit(data.searchQuery);
    }
    if (searchRef.current) {
      searchRef.current.blur();
    }
  };

  const Comp = `h${seoLevel}` as ElementType;
  return (
    <div className="SearchBar">
      <Form
        role="search"
        onSubmit={handleSubmit}
        className="SearchBar__container grid gap-comfy-sm"
        options={{
          defaultValues: {
            searchQuery: defaultQuery,
          },
        }}
      >
        {title && (
          <Text
            size={titleSize as Sizes}
            prominence={Prominence.Potent}
            variant={Variants.Heading}
            asChild
          >
            <Comp className="heading-neutral-amplified">{title}</Comp>
          </Text>
        )}
        <div className="SearchBar__input flex flex-row gap-comfy-sm">
          <InputField
            name="searchQuery"
            inputMode="search"
            aria-label="search"
            placeholder={placeholder}
            iconName="search-sm"
            iconClasses="searchIcon"
            onChange={
              enableQuickSearch
                ? (e) => {
                    trackQueryChange(e.target.value);
                    handleQueryChange(e.target.value);
                  }
                : undefined
            }
            inputRef={searchRef}
          />
          {!enableQuickSearch && (
            <ButtonStyle size="md">
              <button
                type="submit"
                onClick={() => {
                  trackClick(buttonText || t('skippy.article.search'));
                }}
              >
                {buttonText || t('skippy.article.search')}
              </button>
            </ButtonStyle>
          )}
        </div>
      </Form>
    </div>
  );
};
